import { useCallback, useMemo, useState, type ChangeEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import { Drawer } from "../Drawer";
import { trpc } from "../../lib/trpc";

export type ImportKind = "stock" | "semi" | "finished" | "supplier" | "customer";

const KIND_LABELS: Record<ImportKind, { title: string; columns: string[]; sampleRow: Record<string, string | number> }> = {
  stock: {
    title: "Stok Kartları",
    columns: ["Kod", "Ad", "Açıklama", "Birim", "Raf Ömrü", "Alerjenler", "Sipariş Noktası", "Tedarik Süresi"],
    sampleRow: { Kod: "HM-UN-T55", Ad: "Un Tip 550", Açıklama: "", Birim: "kg", "Raf Ömrü": 180, Alerjenler: "gluten", "Sipariş Noktası": 500, "Tedarik Süresi": 7 },
  },
  semi: {
    title: "Yarı Mamuller",
    columns: ["Kod", "Ad", "Açıklama", "Birim", "Raf Ömrü", "Alerjenler", "QC", "Tüketim"],
    sampleRow: { Kod: "YM-HAMUR-T1", Ad: "Tortilla Hamuru", Açıklama: "", Birim: "kg", "Raf Ömrü": 1, Alerjenler: "gluten", QC: "Hayır", Tüketim: "backflush" },
  },
  finished: {
    title: "Mamuller",
    columns: ["Kod", "Ad", "Açıklama", "Birim", "Raf Ömrü", "Alerjenler", "QC", "Tüketim"],
    sampleRow: { Kod: "MM-TORT-25", Ad: "Tortilla 25cm", Açıklama: "", Birim: "adet", "Raf Ömrü": 45, Alerjenler: "gluten", QC: "Evet", Tüketim: "backflush" },
  },
  supplier: {
    title: "Tedarikçiler",
    columns: ["Kod", "Ad", "Vergi No", "E-posta", "Telefon", "Adres", "Şehir", "Ülke"],
    sampleRow: { Kod: "SUP-001", Ad: "Anadolu Un San. A.Ş.", "Vergi No": "1234567890", "E-posta": "info@anadolu.com", Telefon: "+90 374 123 4567", Adres: "OSB 5. Cad. No: 12", Şehir: "Bolu", Ülke: "TR" },
  },
  customer: {
    title: "Müşteriler",
    columns: ["Kod", "Ad", "Vergi No", "E-posta", "Telefon", "Adres", "Şehir", "Ülke"],
    sampleRow: { Kod: "CUS-001", Ad: "Marketim Zinciri", "Vergi No": "9876543210", "E-posta": "satinalma@marketim.com", Telefon: "+90 212 555 1234", Adres: "Levent Mh.", Şehir: "İstanbul", Ülke: "TR" },
  },
};

interface ImportModalProps {
  open: boolean;
  defaultKind?: ImportKind;
  onClose: () => void;
}

export function ImportModal({ open, defaultKind = "stock", onClose }: ImportModalProps) {
  const [kind, setKind] = useState<ImportKind>(defaultKind);
  const [fileName, setFileName] = useState<string>("");
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [report, setReport] = useState<Awaited<ReturnType<typeof importMut.mutateAsync>> | null>(null);

  const qc = useQueryClient();
  const importMut = trpc.importBulk.bulkUpsert.useMutation();

  const labels = KIND_LABELS[kind];

  const reset = useCallback(() => {
    setFileName("");
    setRows([]);
    setParseError(null);
    setReport(null);
    importMut.reset();
  }, [importMut]);

  function handleClose() {
    reset();
    onClose();
  }

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setParseError(null);
    setReport(null);
    try {
      const ab = await f.arrayBuffer();
      const wb = XLSX.read(ab);
      const sheetName = wb.SheetNames[0];
      if (!sheetName) {
        setParseError("Dosyada sheet bulunamadı.");
        return;
      }
      const sheet = wb.Sheets[sheetName]!;
      const parsed = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: null });
      if (parsed.length === 0) {
        setParseError("Dosya boş veya başlık satırından sonra veri yok.");
        return;
      }
      setRows(parsed);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : String(err));
    }
  }

  function downloadTemplate() {
    const ws = XLSX.utils.json_to_sheet([labels.sampleRow], { header: labels.columns });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, labels.title);
    XLSX.writeFile(wb, `STEPERP_${kind}_template.xlsx`);
  }

  async function handleSubmit() {
    if (rows.length === 0) return;
    const result = await importMut.mutateAsync({ kind, rows: rows as Array<Record<string, string | number | boolean | null>> });
    setReport(result);
    await qc.invalidateQueries();
  }

  const preview = useMemo(() => rows.slice(0, 5), [rows]);

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title="Excel İçeri Aktar"
      subtitle={`${rows.length} satır${fileName ? ` · ${fileName}` : ""}`}
      size="lg"
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={handleClose} disabled={importMut.isPending}>
            {report ? "Kapat" : "İptal"}
          </button>
          {!report ? (
            <button
              type="button"
              className="btn-md primary"
              onClick={handleSubmit}
              disabled={rows.length === 0 || importMut.isPending}
            >
              {importMut.isPending ? "Yükleniyor…" : `${rows.length} Satırı İçeri Aktar`}
            </button>
          ) : null}
        </>
      }
    >
      {!report ? (
        <>
          <div className="form-row">
            <div className="form-field">
              <label className="lbl" htmlFor="im-kind">Tür</label>
              <select
                id="im-kind"
                className="form-select"
                value={kind}
                onChange={(e) => { setKind(e.target.value as ImportKind); reset(); }}
                disabled={importMut.isPending}
              >
                <option value="stock">Stok Kartları</option>
                <option value="semi">Yarı Mamuller</option>
                <option value="finished">Mamuller</option>
                <option value="supplier">Tedarikçiler</option>
                <option value="customer">Müşteriler</option>
              </select>
              <span className="help">Kod varsa güncellenir, yoksa eklenir (upsert).</span>
            </div>
            <div className="form-field">
              <label className="lbl">Şablon</label>
              <button type="button" className="btn-md secondary" onClick={downloadTemplate}>
                Excel şablonu indir
              </button>
              <span className="help">Boş şablon + örnek satır.</span>
            </div>
          </div>

          <div className="form-field">
            <label className="lbl" htmlFor="im-file">Dosya seç (.xlsx, .xls, .csv)</label>
            <input
              id="im-file"
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFile}
              className="form-input"
              style={{ padding: "6px 10px", height: "auto" }}
              disabled={importMut.isPending}
            />
            <span className="help">
              Beklenen kolonlar: <code style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>{labels.columns.join(" · ")}</code>
            </span>
          </div>

          {parseError ? <div className="form-banner-err">{parseError}</div> : null}

          {rows.length > 0 ? (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: "var(--neutral-700)", marginBottom: 6 }}>
                Önizleme (ilk 5 satır / toplam {rows.length})
              </div>
              <div style={{ border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)", overflow: "auto", maxHeight: 220 }}>
                <table style={{ width: "100%", fontSize: 12, borderCollapse: "separate", borderSpacing: 0 }}>
                  <thead>
                    <tr>
                      {labels.columns.map((c) => (
                        <th key={c} style={{
                          textAlign: "left",
                          padding: "6px 10px",
                          background: "var(--neutral-50)",
                          borderBottom: "1px solid var(--neutral-200)",
                          fontSize: 10.5,
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          color: "var(--neutral-600)",
                          whiteSpace: "nowrap",
                        }}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((r, i) => (
                      <tr key={i}>
                        {labels.columns.map((c) => (
                          <td key={c} style={{
                            padding: "6px 10px",
                            borderBottom: "1px solid var(--neutral-100)",
                            whiteSpace: "nowrap",
                            color: r[c] == null || r[c] === "" ? "var(--neutral-400)" : "var(--neutral-900)",
                          }}>
                            {r[c] == null || r[c] === "" ? "—" : String(r[c])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {importMut.error ? <div className="form-banner-err" style={{ marginTop: 12 }}>{importMut.error.message}</div> : null}
        </>
      ) : (
        // Sonuç raporu
        <>
          <div style={{
            background: "var(--success-subtle)",
            border: "1px solid color-mix(in srgb, var(--success) 25%, transparent)",
            borderLeft: "3px solid var(--success)",
            borderRadius: "var(--radius-md)",
            padding: "12px 14px",
            marginBottom: 14,
            fontSize: 13,
            color: "var(--success-text)",
          }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>İçeri aktarma tamamlandı</div>
            <div className="mono" style={{ fontSize: 12 }}>
              Toplam {report.total} · Eklenen {report.created} · Güncellenen {report.updated} · Atlanan {report.skipped} · Hatalı {report.errors.length}
            </div>
          </div>
          {report.errors.length > 0 ? (
            <div>
              <div style={{ fontSize: 12, fontWeight: 500, color: "var(--neutral-700)", marginBottom: 6 }}>
                Hatalar ({report.errors.length})
              </div>
              <div style={{ border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)", overflow: "auto", maxHeight: 280 }}>
                <table style={{ width: "100%", fontSize: 12, borderCollapse: "separate", borderSpacing: 0 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", padding: "6px 10px", background: "var(--neutral-50)", borderBottom: "1px solid var(--neutral-200)", fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "var(--neutral-600)", width: 60 }}>Satır</th>
                      <th style={{ textAlign: "left", padding: "6px 10px", background: "var(--neutral-50)", borderBottom: "1px solid var(--neutral-200)", fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "var(--neutral-600)", width: 140 }}>Kod</th>
                      <th style={{ textAlign: "left", padding: "6px 10px", background: "var(--neutral-50)", borderBottom: "1px solid var(--neutral-200)", fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "var(--neutral-600)" }}>Hata</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.errors.map((e, i) => (
                      <tr key={i}>
                        <td className="mono" style={{ padding: "6px 10px", borderBottom: "1px solid var(--neutral-100)", fontSize: 11.5 }}>{e.row}</td>
                        <td className="mono" style={{ padding: "6px 10px", borderBottom: "1px solid var(--neutral-100)", fontSize: 11.5 }}>{e.code ?? "—"}</td>
                        <td style={{ padding: "6px 10px", borderBottom: "1px solid var(--neutral-100)", color: "var(--danger)" }}>{e.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </>
      )}
    </Drawer>
  );
}
